/**
 * ContratsParametres — Configuration des champs personnalisés contrats.
 */
import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Settings, Plus, Trash2, Save } from "lucide-react";
import { toast } from "sonner";
import ContractsExpirySettings from "@/pages/ContractsExpirySettings";

const TYPE_LABELS = {
  text: "Texte", number: "Nombre", date: "Date",
  boolean: "Oui/Non", select: "Liste déroulante",
};

export default function ContratsParametres() {
  const { hasPermission } = useAuth();
  const canWrite = hasPermission("contracts.write");
  const [fields, setFields] = useState([]);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/contracts/config/custom-fields");
        setFields(r.data.custom_fields_config || []);
      } catch (e) { toast.error(apiError(e)); }
    })();
  }, []);

  const update = (idx, patch) => {
    setFields((prev) => prev.map((f, i) => i === idx ? { ...f, ...patch } : f));
    setDirty(true);
  };
  const add = () => {
    setFields((prev) => [...prev, {
      key: `field_${prev.length + 1}`, label: "Nouveau champ",
      field_type: "text", options: [], required: false,
    }]);
    setDirty(true);
  };
  const remove = (idx) => {
    setFields((prev) => prev.filter((_, i) => i !== idx));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/contracts/config/custom-fields", { custom_fields_config: fields });
      toast.success("Paramètres enregistrés.");
      setDirty(false);
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6 max-w-4xl" data-testid="contrats-parametres-page">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-900 flex items-center gap-2"
              style={{ fontFamily: "Manrope, sans-serif" }}>
            <Settings className="h-7 w-7 text-slate-600" /> Paramètres Contrats
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Configurez les champs personnalisés visibles sur chaque contrat.
          </p>
        </div>
        {canWrite && (
          <Button onClick={save} disabled={!dirty || saving}
                   className="bg-blue-600 hover:bg-blue-700 text-white"
                   data-testid="params-save-btn">
            <Save className="w-4 h-4 mr-1.5" />
            {saving ? "Enregistrement…" : "Enregistrer"}
          </Button>
        )}
      </div>

      <Card><CardContent className="p-5 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold">Champs personnalisés</h2>
          {canWrite && (
            <Button variant="outline" size="sm" onClick={add}
                     data-testid="params-add-field-btn">
              <Plus className="w-3.5 h-3.5 mr-1" /> Ajouter un champ
            </Button>
          )}
        </div>
        {fields.length === 0 && (
          <div className="text-sm text-slate-400 text-center py-6"
                data-testid="params-empty-state">
            Aucun champ personnalisé configuré. Ajoutez-en pour enrichir vos contrats.
          </div>
        )}
        {fields.map((f, idx) => (
          <div key={idx} className="grid grid-cols-12 gap-3 items-end p-3 border border-slate-200 rounded-lg"
                data-testid={`field-row-${idx}`}>
            <div className="col-span-3">
              <Label className="text-[10px]">Clé (a-z, 0-9, _)</Label>
              <Input value={f.key} onChange={(e) => update(idx, { key: e.target.value })}
                      className="mt-1 font-mono text-xs" disabled={!canWrite} />
            </div>
            <div className="col-span-4">
              <Label className="text-[10px]">Libellé</Label>
              <Input value={f.label} onChange={(e) => update(idx, { label: e.target.value })}
                      className="mt-1" disabled={!canWrite} />
            </div>
            <div className="col-span-2">
              <Label className="text-[10px]">Type</Label>
              <Select value={f.field_type} onValueChange={(v) => update(idx, { field_type: v })}
                       disabled={!canWrite}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(TYPE_LABELS).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2 flex items-center gap-2 pb-1">
              <Switch checked={f.required} onCheckedChange={(v) => update(idx, { required: v })}
                       disabled={!canWrite} />
              <Label className="text-xs">Obligatoire</Label>
            </div>
            <div className="col-span-1">
              {canWrite && (
                <Button variant="outline" size="sm" onClick={() => remove(idx)}
                         className="w-full border-red-200 text-red-600 hover:bg-red-50">
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              )}
            </div>
            {f.field_type === "select" && (
              <div className="col-span-12">
                <Label className="text-[10px]">Options (séparées par virgule)</Label>
                <Input value={(f.options || []).join(", ")}
                        onChange={(e) => update(idx, {
                          options: e.target.value.split(",").map((s) => s.trim()).filter(Boolean),
                        })}
                        className="mt-1" disabled={!canWrite} />
              </div>
            )}
          </div>
        ))}
      </CardContent></Card>

      {/* Alertes de fin de contrat — configuration user (seuils J-N + emails externes) */}
      <ContractsExpirySettings />
    </div>
  );
}
